import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API, getContentSource } from "@/lib/constants";
import { hubspotContext } from "@/lib/hubspot";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HUBSPOT_MEETING_BASE, MEETING_LINKS } from "@/lib/meetingLinks";

const SEO = {
  title: "Contact Dallas SEO Company | Book Free Consultation | Digital Media Ninja DFW",
  description: "Contact Dallas's top digital marketing agency. Book a free SEO consultation, get a marketing audit, or schedule a strategy call. Located in Dallas, TX serving all of DFW. Schedule your meeting today!",
  keywords: "contact Dallas SEO company, free SEO consultation Dallas, digital marketing consultation DFW, Dallas marketing agency contact, schedule marketing call Dallas, book SEO audit Fort Worth",
  image: "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG",
  url: "https://digitalmedianinja.com/contact"
};

const ContactPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', service: 'general', budget: '', message: '', website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState('general');

  useEffect(() => {
    const topic = searchParams.get('topic');
    if (topic === 'gym') setFormData((f) => ({ ...f, message: "I'd like a free gym growth plan." }));
    if (topic === 'gym-website') setFormData((f) => ({ ...f, message: "I'd like a free growth plan for a new gym website." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = SEO.title;
    
    // Load HubSpot meeting embed script
    const script = document.createElement('script');
    script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, { ...formData, ...hubspotContext() });
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({ name: '', email: '', phone: '', company: '', service: 'general', budget: '', message: '', website: '' });
      navigate("/thank-you/lead?from=contact");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const meetingTypes = [
    { value: 'general', label: 'General Consultation', desc: 'Free 30-min strategy call' },
    { value: 'seo', label: 'SEO Strategy Call', desc: 'Review your SEO opportunities' },
    { value: 'aeo', label: 'AEO Strategy Call', desc: 'AI Search Optimization' },
    { value: 'geo', label: 'GEO Strategy Call', desc: 'Generative Engine Optimization' },
    { value: 'ppc', label: 'PPC Strategy Call', desc: 'Google Ads consultation' },
    { value: 'webDesign', label: 'Web Design Consultation', desc: 'Website redesign discussion' },
  ];

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="contact-page">
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <meta name="keywords" content={SEO.keywords} />
        <link rel="canonical" href={SEO.url} />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:image" content={SEO.image} />
        <meta property="og:url" content={SEO.url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
      </Helmet>

      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#00FF85] uppercase tracking-wider text-sm font-medium">Contact Us</span>
          <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Book Your Free Strategy Call
          </h1>
          <p className="text-gray-400 text-lg">
            Schedule a consultation with our Dallas marketing experts. Choose a time that works for you and let's discuss how we can grow your business.
          </p>
        </div>

        {/* Meeting Type Selection */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-xl font-bold text-white text-center mb-6">What would you like to discuss?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {meetingTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setSelectedMeeting(type.value)}
                className={`p-4 rounded-xl border text-center transition-all ${
                  selectedMeeting === type.value
                    ? 'border-[#00FF85] bg-[#00FF85]/10 text-white'
                    : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/30'
                }`}
              >
                <p className="font-medium text-sm mb-1">{type.label}</p>
                <p className="text-xs opacity-70">{type.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* HubSpot Calendar Embed */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="bg-[#0A0A0A] border-white/10 overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-[#00FF85]/10 to-transparent p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#00FF85]/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#00FF85]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Schedule Your Free Consultation</h3>
                    <p className="text-gray-400 text-sm">Pick a time that works for you • Synced with Google Calendar</p>
                  </div>
                </div>
              </div>
              <div className="meetings-iframe-container" style={{ minHeight: '700px' }}>
                <iframe
                  src={`${MEETING_LINKS[selectedMeeting] || HUBSPOT_MEETING_BASE}?embed=true`}
                  width="100%"
                  height="700"
                  frameBorder="0"
                  title="Schedule a meeting"
                  className="bg-white"
                  data-testid="hubspot-calendar-embed"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <Card className="bg-[#0A0A0A] border-white/5 p-6">
              <CardContent className="p-0 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00FF85]/20 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#00FF85]" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Phone</h3>
                    <a href="tel:+14329349470" className="text-gray-400 hover:text-[#00FF85]">(432) 934-9470</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00FF85]/20 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#00FF85]" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Email</h3>
                    <a href="mailto:info@digitalmedianinja.com" className="text-gray-400 hover:text-[#00FF85]">info@digitalmedianinja.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00FF85]/20 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#00FF85]" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Address</h3>
                    <a 
                      href="https://www.google.com/maps/place/Digital+Media+Ninja/@32.7976692,-96.814698,17z"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#00FF85]"
                    >
                      3333 Harry Hines Blvd<br />Dallas, TX 75201
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00FF85]/20 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#00FF85]" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Hours</h3>
                    <p className="text-gray-400">Mon-Fri: 9am - 5pm CST</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-white/5 p-6">
              <CardContent className="p-0">
                <h3 className="font-bold mb-4">What You&apos;ll Get:</h3>
                <ul className="space-y-3">
                  {[
                    "Free comprehensive SEO audit",
                    "Custom strategy recommendations",
                    "Competitor analysis report",
                    "No-obligation consultation"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-400">
                      <CheckCircle2 className="w-4 h-4 text-[#00FF85]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-[#0A0A0A] border-white/5 p-8">
              <h3 className="text-xl font-bold mb-6">Prefer to send a message?</h3>
              <form onSubmit={handleSubmit} id="contact-form" name="contact-form" className="space-y-6" data-testid="contact-form">
                <input type="hidden" name="content_source" value={getContentSource()} />
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <Input
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="form-input"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="form-input"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      name="phone"
                      placeholder="(123) 456-7890"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="form-input"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Company</label>
                    <Input
                      name="company"
                      placeholder="Your Company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="form-input"
                      data-testid="contact-company"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Service Interest</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                      className="form-input w-full"
                      data-testid="contact-service"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="local-seo">Local SEO</option>
                      <option value="aeo">Answer Engine Optimization</option>
                      <option value="geo">Generative Engine Optimization</option>
                      <option value="ppc">PPC Advertising</option>
                      <option value="web-design">Web Design</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Website</label>
                    <Input
                      name="website"
                      placeholder="https://yourwebsite.com"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      className="form-input"
                      data-testid="contact-website"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <Textarea
                    name="message"
                    placeholder="Tell us about your project and goals..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="form-input min-h-[150px]"
                    required
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-ninja w-full py-6 text-lg"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you agree to receive marketing communications. We respect your privacy.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
